import {
  TeamOutlined,
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
  UsergroupAddOutlined
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import {
  Button,
  Popover,
  Drawer,
  Form,
  Input,
  Space,
  Select,
} from "antd";
import ExcelAddinUsersService from "../../../../../services/excel-addin/UsersService";
import Datatable from "../../../../shared/components/Datatable";
import { UpdateUserRequest } from "../../../../../models/ExcelAddin/UpdateUserRequest";
import { CreateUserRequest } from "../../../../../models/ExcelAddin/CreateUserRequest";
import notificationUtil from "../../../../shared/utils/NotificationsUtil";
import { NOTIFICATION_TYPES } from "../../../../../config/constants";
import styles from "./UserManagement.module.css";
import { UserRoleFilter } from "../../../../../models/ExcelAddin/UserRoleFilter";

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const UserManagement = ({ openNewUserDrawer, setOpenNewUserDrawer, editAllowed, data, setData, roles, fetchData, isTableLoading }) => {

  const [editedRow, setEditedRow] = useState<User>();

  //drawer
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const placement = "bottom";

  //form
  const [loadingButton, setLoadingButton] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [form] = Form.useForm();
  const [rolesInput, setRolesInput] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [deletedRoleIds, setDeletedRoleIds] = useState<number[]>([]);
  const [selectedFilterRoleId, setSelectedFilterRoleId] = useState<number>();
  const [editedUserRoleFilters, setEditedUserRoleFilters] = useState<UserRoleFilter[]>([]);

  interface User {
    id: number;
    email: string;
    userName: string;
    userDescription: string;
    roles: Role[];
  }

  interface Role {
    id: number;
    description: string;
    filter: string;
  }

  useEffect(() => {
    if (openNewUserDrawer) {
      showDrawer(RowAction.Create);
    }
  }, [openNewUserDrawer]);

  const showRoles = (roles: Role[]) => {
    return (
        <div>
          {roles.map(function (d, id) {
            return <li key={id}>{d.description}</li>;
          })}
        </div>
    );
  };

  const onDoubleClickRow = (record: User) => {
    showDrawer(RowAction.View);
    setForm(record);
    setEditedRow(record);
  };

  const onEditUser = (record: User) => {
    showDrawer(RowAction.Edit);
    setForm(record);
    setEditedRow(record);
  };

  const onCloseDrawer = () => {
    setOpenDrawer(false);
    setOpenNewUserDrawer(false);
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setRolesInput([]);
    setEditedRow(undefined);
    setDeletedRoleIds([]);
    setEditedRow(undefined);
    setSelectedRole(null);
    setSelectedFilterRoleId(undefined);
    setEditedUserRoleFilters([]);
    setIsValidForm(false);
  };

  const onSaveUser = () => {
    setLoadingButton(true);

    if (editedRow?.id) {
      const userToUpdate: UpdateUserRequest = {
        email: form.getFieldValue("email"),
        userName: form.getFieldValue("userName"),
        userDescription: form.getFieldValue("userDescription"),
        roleIds: rolesInput.map(x => x.id),
        deletedRoleIds: deletedRoleIds,
        editedUserRoleFilters: editedUserRoleFilters,
      }

      ExcelAddinUsersService.update(editedRow.id, userToUpdate)
          .then(() => {
            fetchData();
            onCloseDrawer();
            notificationUtil.openNotificationDialog({ description: "User edited successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
          })
          .catch(error => {
            notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false });
          })
          .finally(() => {
            {
              setLoadingButton(false);
            }
          });

    } else {
      const newUser: CreateUserRequest = {
        email: form.getFieldValue("email"),
        userName: form.getFieldValue("userName"),
        userDescription: form.getFieldValue("userDescription"),
        roleIds: rolesInput.map(x => x.id),
      }

      ExcelAddinUsersService.create(newUser)
          .then(res => {
            fetchData();
            onCloseDrawer();

            notificationUtil.openNotificationDialog({ description: "User created successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
          })
          .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
          .finally(() => {
            setLoadingButton(false);
          });

    }
  }

  const onDeleteUser = (id: number) => {
    ExcelAddinUsersService.remove(id)
        .then((response) => {
          fetchData();
          notificationUtil.openNotificationDialog({ description: "User deleted successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
        })
        .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }));
  };

  const onAddRoleClick = () => {
    if (selectedRole) {
      setRolesInput([...rolesInput, selectedRole]);
      setDeletedRoleIds(deletedRoleIds.filter(id => id !== selectedRole.id));
      setSelectedRole(null);
    }
    onFieldsChange();
  }

  const onDeleteRole = (id: number) => {
    setRolesInput(rolesInput.filter(u => u.id !== id));
    setDeletedRoleIds([...deletedRoleIds, id]);
    onFieldsChange();
  };

  const setForm = (user: User) => {
    form.setFieldsValue({
      userName: user.userName,
      email: user.email,
      userDescription: user.userDescription,
    });

    setRolesInput(user.roles);
  }

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle("User details");
        break;
      case RowAction.Create:
        form.resetFields();
        setDrawerTitle("New user");
        break;
      case RowAction.Edit:
        setDrawerTitle("Edit user");
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onFieldsChange = () => {
    setIsValidForm(form.getFieldsError().filter(({ errors }) => errors.length).length === 0);
  }

  const onEditFilter = (record: Role) => {
    setSelectedFilterRoleId(record.id);
  }

  const onFilterChange = (value: string) => {
    setRolesInput(rolesInput.map(u => {
          if (u.id === selectedFilterRoleId) {
            return {
              ...u,
              filter: value
            }
          }
          return u;
        })
    );


    //if filter exists, update it, if not, add it
    const existingFilter = editedUserRoleFilters.find(u => u.roleId === selectedFilterRoleId && u.userId === editedRow.id);
    if (existingFilter) {
      setEditedUserRoleFilters(editedUserRoleFilters.map(u => {
        if (u.roleId === selectedFilterRoleId && u.userId === editedRow.id) {
          return {
            ...u,
            filter: value
          }
        }
        return u;
      }));
    } else {
      setEditedUserRoleFilters([...editedUserRoleFilters, { roleId: selectedFilterRoleId, userId: editedRow.id, filter: value }]);
    }

    onFieldsChange();
  }


  const scroll = { y: "calc(100vh - 320px)" };

  const columns = [
    {
      title: "Name",
      dataIndex: "userName",
      key: "userName",
      filters: [],
      filterMode: "tree",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      filters: [],
      filterMode: "tree",
    },
    {
      title: "Roles",
      dataIndex: "id",
      key: "id",
      filters: [],
      filterMode: "tree",
      render: (val, rec) => (
          <Popover content={() => showRoles(rec.roles)} title="Roles">
            <Button icon={<TeamOutlined />} className={"active-btn"} size="small">
              Roles
            </Button>
          </Popover>
      ),
    },
    {
      title: "Description",
      dataIndex: "userDescription",
      key: "userDescription",
      filters: [],
      filterMode: "tree",
    },
  ];

  const editRolesColumns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      filters: [],
      filterMode: "tree",
    },
    {
      title: "Filter",
      dataIndex: "filter",
      key: "filter",
      filters: [],
      filterMode: "tree",
      render(value, record, index) {
        if (selectedFilterRoleId === record.id) {
          return <Input value={value} onChange={(e) => onFilterChange(e.target.value)} />
        } else {
          return value;
        }
      }
    },
  ];

  return (
      <div>
        <Datatable
            scroll={scroll}
            tableColumns={columns}
            data={data}
            setData={setData}
            isTableLoading={isTableLoading}
            editRowEnabled={editAllowed}
            deleteRowEnabled={editAllowed}
            onEditFunc={onEditUser}
            onDeleteFunc={onDeleteUser}
            onDoubleClickRow={onDoubleClickRow}
            showActionsColumn={editAllowed}
            paginationDisabled={true}
        ></Datatable>
        <Drawer
            width={"400px"}
            getContainer={false}
            className={styles.drawer}
            title={drawerTitle}
            placement={placement}
            onClose={onCloseDrawer}
            visible={openDrawer}
            closable={false}
            height={"75%"}
            extra={
              <Space>
                {RowAction?.View === drawerAction && (
                    <>
                      <Button
                          onClick={() => showDrawer(RowAction.Edit)}
                          icon={<EditOutlined />}
                          className={"active-btn"}
                          size="small"
                          disabled={!editAllowed}
                          hidden={!editAllowed}
                      >
                        Edit
                      </Button>
                      <Button
                          type="ghost"
                          onClick={onCloseDrawer}
                          icon={<CloseOutlined />}
                          size="small"
                      />
                    </>
                )}
                {RowAction?.Create === drawerAction && (
                    <>
                      <Button
                          onClick={onSaveUser}
                          icon={<CheckOutlined />}
                          size="small"
                          loading={loadingButton}
                          disabled={!isValidForm}
                      >
                        Save
                      </Button>
                      <Button
                          type="ghost"
                          onClick={onCloseDrawer}
                          icon={<CloseOutlined />}
                          size="small"
                      />
                    </>
                )}
                {RowAction?.Edit === drawerAction && (
                    <>
                      <Button
                          onClick={onSaveUser}
                          icon={<CheckOutlined />}
                          size="small"
                          loading={loadingButton}
                          disabled={!isValidForm}
                      >
                        Update
                      </Button>
                      <Button
                          type="ghost"
                          onClick={onCloseDrawer}
                          icon={<CloseOutlined />}
                          size="small"
                      />
                    </>
                )}
              </Space>
            }
        >
          <Form form={form} onFieldsChange={onFieldsChange} disabled={RowAction.View === drawerAction ? true : false} layout="vertical">
            <div className={styles.columnsWrap}>
              <div className={styles.halfWidth}>
                <Form.Item label="Username:"
                           name="userName"
                           rules={[
                             {
                               required: true,
                             },
                           ]}
                >
                  <Input
                      allowClear
                      placeholder="enter value"
                  ></Input>
                </Form.Item>
                <Form.Item label="Email:" name="email"
                           rules={[
                             {
                               type: 'email',
                               message: 'no valid E-mail',
                             },
                           ]}
                >
                  <Input
                      allowClear
                      placeholder="enter value"
                  ></Input>
                </Form.Item>
                <Form.Item label="Description:" name="userDescription">
                  <Input
                      allowClear
                      placeholder="enter value"
                  ></Input>
                </Form.Item>
              </div>
              <div className={styles.halfWidth}>
                <Form.Item label="Add Role:">
                  <div
                      className={styles.flexRow}
                  >
                    <Select
                        allowClear
                        showSearch
                        className={styles.fullWidth}
                        placeholder="Please select"
                        filterSort={(optionA, optionB) =>
                            (optionA!.label as unknown as string)
                                .toLowerCase()
                                .localeCompare(
                                    (optionB!.label as unknown as string).toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        options={roles
                            .filter(u => {
                              return !rolesInput.map(u => u.id).includes(u.id)
                            })
                            .map(u => {
                              return {
                                label: u.description,
                                value: u.id,
                              }
                            })
                        }
                        value={selectedRole?.id}
                        onChange={(userId) => {
                          var user = roles.find(u => u.id === userId);
                          setSelectedRole(user);
                        }}
                    />
                    <Button
                        icon={<UsergroupAddOutlined />}
                        type="primary"
                        className={styles.green_btn}
                        onClick={() => onAddRoleClick()}
                    />
                  </div>
                </Form.Item>
                <Form.Item label="Edit Roles:">
                  <Datatable
                      scroll={scroll}
                      tableColumns={editRolesColumns}
                      data={rolesInput}
                      setData={setRolesInput}
                      isTableLoading={isTableLoading}
                      deleteRowEnabled={editAllowed}
                      onDeleteFunc={onDeleteRole}
                      onEditFunc={onEditFilter}
                      editRowEnabled={editAllowed && RowAction?.Edit === drawerAction}
                      showActionsColumn={editAllowed}
                  ></Datatable>
                </Form.Item>
              </div>
            </div>
          </Form>
        </Drawer>
      </div>
  );
};

export { UserManagement };
